/* eslint-disable no-unused-vars */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Alert } from '@mui/material';
import { getComponentByType } from '../editorComponents';

const RECORDS_PER_PAGE = 10;

function defaultPageSelector(rows) {
  return [...Array(Math.floor(rows.length / RECORDS_PER_PAGE) + 1)].map((_, i) => ({
    label: `${i + 1}`,
    id: i,
    rows: rows.slice(i * RECORDS_PER_PAGE, (i + 1) * RECORDS_PER_PAGE),
  }));
}

// eslint-disable-next-line import/prefer-default-export
export const useTablePart = ({
  data, options, onChange, readOnly = false,
  overrideColumns = null, pagesSelector = defaultPageSelector,
  defaultRecord = null,
}) => {
  const refedRows = useRef([]);
  const [controls, setControls] = useState({});
  const rows = useMemo(
    () => (data || []).map((row, n) => (row.id ? row : { ...row, id: -n })),
    [data],
  );
  refedRows.current = rows;
  const fields = useMemo(
    () => ({ ...options.child.children, ...(overrideColumns || {}) }),
    [options.child.children, overrideColumns],
  );

  const [activeRow, setActiveRow] = useState(null);

  const [currentPage, setCurrentPage] = useState(false);

  // TODO: Применить фильтрацию
  const visibleRows = useMemo(
    () => rows,
    [rows],
  );

  const pages = useMemo(
    () => pagesSelector(visibleRows),
    [pagesSelector, visibleRows],
  );

  useEffect(() => {
    if (!pages.filter((p) => p.id === currentPage).length && pages.length) {
      setCurrentPage(pages[0].id);
    }
  }, [currentPage, pages]);

  const displayedItems = useMemo(
    () => pages.filter((p) => p.id === currentPage).reduce((R, r) => r.rows, visibleRows),
    [currentPage, pages, visibleRows],
  );

  const onActivateRow = useCallback(
    (e, rowId) => setActiveRow(rowId),
    [],
  );

  const onAddRow = useCallback(
    (e) => {
      onChange(e, [
        ...refedRows.current,
        defaultRecord ? defaultRecord() : {}]);
    },
    [defaultRecord, onChange],
  );

  const onDeleteRow = useCallback(
    (e, rowId) => onChange(e, refedRows.current
      .filter((i) => i.id !== rowId)),
    [onChange],
  );
  const onRowChange = useCallback(
    (e, rowId, partValue) => onChange(e, refedRows.current.map(
      (row) => (row.id === rowId ? { ...row, ...partValue } : row),
    )),
    [onChange],
  );
  const onChangePage = useCallback(
    (e, pageId) => setCurrentPage(pageId),
    [],
  );

  const actions = useMemo(
    () => ({
      onActivateRow,
      onAddRow,
      onDeleteRow,
      onChange: onRowChange,
      onChangePage,
    }),
    [onActivateRow, onAddRow, onChangePage, onDeleteRow, onRowChange],
  );

  const permissions = useMemo(
    () => ({
      canAdd: !readOnly,
      canDelete: !readOnly && !!displayedItems.filter((i) => i.id === activeRow).length,
    }),
    [activeRow, displayedItems, readOnly],
  );

  function getBaseProps(field) {
    if (!field) return {};
    switch (field.type) {
      case 'string':
        return {
          maxLength: field.max_length || 0,
        };
      case 'choice':
        return {
          values: field.choices || [],
        };
      case 'nested object':
        return {
          columns: field.children,
          backend: field.resource,
        };
      case 'decimal':
        return {
          precision: 2,
        };
      default:
        return {};
    }
  }

  function getBaseFieldName(field) {
    if (!fields[field]) return '';
    if (fields[field].type === 'nested object' && fields[field].read_only && fields[`${field}_id`]) {
      return `${field}_id`;
    }
    if (fields[field].type === 'field' && fields[field].read_only && fields[`${field}_id`]) {
      return `${field}_id`;
    }
    return field;
  }

  const lcontrols = Object.keys(fields)
    .reduce((R, k) => (
      {
        ...R,
        [k]: getComponentByType(
          fields[k],
          fields[getBaseFieldName(k, fields)].read_only,
        ),
      }
    ), {});

  const getControlTP = useCallback(
    (field, rowId, newProps = {}) => {
      const currentRow = refedRows.current.filter((el) => (el.id === rowId))[0];
      const Control = lcontrols[field];
      const { callbackRow } = newProps;

      if (!Control || !(currentRow ? currentRow[field] !== 'undefined' : false)) {
        return (
          <Alert severity="error">
            {`Not found coltrol for field "${field}"`}
          </Alert>
        );
      }
      const baseProps = getBaseProps(fields[field]);

      return (
        <Control
          value={currentRow[field]}
          onChange={(e, v) => {
            if (callbackRow) {
              callbackRow({
                onRowChange, e, rowId, v,
              });
            } else onRowChange(e, rowId, { [field]: v });
          }}
          label=""
          placeholder={fields[field].label}
          {...baseProps}
          readOnly={readOnly || fields[field].read_only}
          {...newProps}
        />
      );
    },
    [fields, lcontrols, onRowChange, readOnly],
  );

  return {
    displayedItems,
    fields,
    pages,
    currentPage,
    activeRow,
    actions,
    permissions,
    getControlTP,
  };
};
